import styled from "@emotion/styled";
import {
  MOBILE_SCREEN,
  COLOR_TERTIARY,
  COLOR_APP_TEXT,
  PROCESS_OVERVIEW_BACKGROUND_COLOR_GRADIENT,
  BORDER_RADIUS_MEDIUM,
  FLEX_COLUMN,
  FLEX_ROW,
} from "../../components/theme";
import {
  ParagraphHeaderLarge,
  ParagraphHeader,
} from "../../components/typography/Paragraph";
import OptionsList from "../../components/icons/OptionsList";
import Message from "../../components/icons/Message";
import Review from "../../components/icons/Review";
import Collaborate from "../../components/icons/Collaborate";
import { useGetCurrentUserQuery } from "../../api/users";
import { useGetDocumentsByUserIdQuery } from "../../api/documents";
import { useGetUserStateByIdQuery } from "../../api/states";
import { useGetAttorneyServicesPurchasedByUserQuery } from "../../api/payments";
import { skipToken } from "@reduxjs/toolkit/query";
import Loading from "../../components/Loading";

const StyledProcessOverview = styled.div`
  ${FLEX_COLUMN}
  margin: 40px 0;
  border-radius: ${BORDER_RADIUS_MEDIUM};
  background: ${PROCESS_OVERVIEW_BACKGROUND_COLOR_GRADIENT};
  padding: 48px;
  gap: 16px;
  ${MOBILE_SCREEN} {
    padding: 24px;
  }
`;

const StyledProcessOverviewItems = styled.div`
  ${FLEX_ROW}
  gap: 16px;
  ${MOBILE_SCREEN} {
    ${FLEX_COLUMN}
  }
`;

const StyledProcessOverviewItem = styled.div`
  ${FLEX_COLUMN}
  gap: 16px;
  padding: 24px;
  flex: 1;
  border-radius: 16px;
  background-color: ${COLOR_TERTIARY};
  ${MOBILE_SCREEN} {
    ${FLEX_ROW}
    align-items: center;
  }
`;

const StyledIconWrapper = styled.div`
  display: flex;
  height: 64px;
  ${MOBILE_SCREEN} {
    height: 100%;
  }
`;

const LoadingContainer = styled(Loading)`
  margin-top: 24px;
`;

const ProcessOverview = () => {
  // Get current user
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetCurrentUserQuery();

  // Get user documents
  const {
    data: userDocuments,
    isLoading: isUserDocumentsLoading,
    isError: isUserDocumentsError,
  } = useGetDocumentsByUserIdQuery(user?.id ?? skipToken);
  // Get the first document if available
  const userDocument =
    userDocuments && userDocuments.length > 0 ? userDocuments[0] : null;

  // Get user state
  const {
    data: userState,
    isLoading: isUserStateLoading,
    isError: isUserStateError,
  } = useGetUserStateByIdQuery(
    {
      id: user?.id ?? "",
      stateId: userDocument?.stateId ?? "",
    },
    {
      skip: !userDocument?.stateId,
    },
  );

  // Get attorney services purchased by user
  const {
    data: attorneyServicesPurchased,
    isLoading: isAttorneyServicesPurchasedLoading,
    isError: isAttorneyServicesPurchasedError,
  } = useGetAttorneyServicesPurchasedByUserQuery(
    {
      id: user?.id ?? "",
    },
    {
      skip: !user?.id,
      refetchOnMountOrArgChange: true,
    },
  );

  /**
   * We can update this eventually to be connected to how they answer
   * the "what are you planning to get from this prenup" question
   */
  const connectText =
    userState?.spousalSupportLaw === "ATTORNEY_REQUIRED" &&
    userState.name === "California"
      ? `Connect with an attorney if needed.`
      : userState?.spousalSupportLaw === "ATTORNEY_REQUIRED"
        ? `Connect with an attorney if needed.`
        : attorneyServicesPurchased && attorneyServicesPurchased.length > 0
          ? "Connect with an attorney"
          : "Optional: Connect with an attorney";

  /**
   * If the user state allows online notary, e-sign & notarize your completed prenup
   * Otherwise, notarize your completed prenup
   */
  const notaryTitleText = userState?.allowsOnlineNotary
    ? `E-sign & notarize your completed prenup`
    : "Notarize your completed prenup";

  const processOverviewItems = [
    { title: "Fill out your questionnaire", icon: <OptionsList /> },
    ...(userState?.attorneySupport === "PARTNER"
      ? [{ title: connectText, icon: <Message /> }]
      : userState?.attorneySupport === "NONE"
        ? [{ title: "Collaborate with your Partner", icon: <Collaborate /> }]
        : []),
    { title: notaryTitleText, icon: <Review /> },
  ];

  if (
    isUserLoading ||
    isUserDocumentsLoading ||
    isUserStateLoading ||
    isAttorneyServicesPurchasedLoading
  ) {
    return <LoadingContainer />;
  }

  if (
    isUserError ||
    isUserDocumentsError ||
    isUserStateError ||
    isAttorneyServicesPurchasedError
  ) {
    return null;
  }

  return (
    <StyledProcessOverview>
      <ParagraphHeaderLarge>
        Here&#39;s what to expect for this process:
      </ParagraphHeaderLarge>
      <StyledProcessOverviewItems>
        {processOverviewItems.map((item) => (
          <StyledProcessOverviewItem key={item.title}>
            <StyledIconWrapper>{item.icon}</StyledIconWrapper>
            <ParagraphHeader color={COLOR_APP_TEXT}>
              {item.title}
            </ParagraphHeader>
          </StyledProcessOverviewItem>
        ))}
      </StyledProcessOverviewItems>
    </StyledProcessOverview>
  );
};

export default ProcessOverview;
